/* eslint-disable max-lines -- Pre-existing violation */
import {Helmet} from '@dr.pogodin/react-helmet';
import {isFeatureEnabled} from 'feature-flags';
import {
  closeCurrentDialog,
  DataStatus,
  DropdownActionMenuItem,
  FlagProps,
  openDeleteDialog,
  openDialog,
  Parameter,
  showNotification,
} from 'platform/components';
import {match, Pattern} from 'ts-pattern';

import {any, head, isNil} from 'ramda';
import {isNilOrEmpty, isNotNil, isTrue} from 'ramda-adjunct';

import {useAssistantEntityContext} from '@dms/ai-assistant';
import {
  useGetParticipationQuery,
  useGetTireSetCountQuery,
  useGetTireVehicleCountQuery,
} from '@dms/api/core';
import {
  useDeleteCustomerMutation,
  useGetCustomerContractsByCustomerIdQuery,
  useGetCustomerV2Query,
  useGetServiceContractsQuery,
} from '@dms/api/customer';
import {BusinessCaseState} from '@dms/api/finance';
import {
  useCreateBusinessCaseMutation,
  useGetLastActiveBusinessCaseByCustomerQuery,
  useGetVehicleV2Query,
} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {useGetDirectSaleLastActiveByCustomerIdQuery} from '@dms/api/warehouse';
import {useGetCustomerLastActiveServiceCaseQuery} from '@dms/api/workshop';
import {featureFlags} from '@dms/feature-flags';
import {CustomNoticeList, useCustomNoticesBadge} from '@dms/features/custom-notices';
import {GDPR_CONSENTS_GRID_CODE, GdprConsentsList} from '@dms/features/document-context';
import {TaskGridPage, useTasksCount} from '@dms/features/tasks';
import {useCallCustomerAction} from '@dms/features/telephony-call';
import i18n from '@dms/i18n';
import {
  businessCaseRoutes,
  customerRoutes,
  interestRoutes,
  testIds,
  warehouseRoutes,
  workshopRoutes,
} from '@dms/routes';
import {
  Checklists,
  DetailTemplateHeader,
  EMPTY_PLACEHOLDER,
  EntityHeader,
  EntityHeaderAction,
  getBusinessCaseStateFlag,
  getBusinessCaseTypeFlag,
  getCustomerEmail,
  getCustomerName,
  getLocaleLabel,
  useTenant,
  getCustomerPhoneNumber,
  handleApiError,
  is404ApiError,
  isCustomerNaturalPerson,
  CommunicationSection,
  getCommunicationSectionTitle,
  NavigationItem,
  Page,
  Section,
  SnippetProps,
  toMenuBadge,
  useChecklist,
  useDataGridTotalRows,
  useCustomerUrl,
  useDocumentContextCount,
  useIsLicenceEnabled,
  usePermissions,
  useStateFlag,
  useRecordCount,
  useEmailConfig,
  useKonzultaSmsAvailability,
} from '@dms/shared';
import {SendCustomerEmailForm, SendCustomerSmsForm} from '@dms/shared-aftersales';

import {ApiError, buildArray, buildObject, composePath, useNavigate} from 'shared/web';

import {customerSearchParams} from '../../customerSearchParams';
import {getCustomerAssistantEntities} from '../../utils/getCustomerAssistantEntities';
import {BusinessCases} from './(sections)/BusinessCases/BusinessCases';
import {CustomerActivityLogs} from './(sections)/CustomerActivityLogs/CustomerActivityLogs';
import {CustomerContracts} from './(sections)/CustomerContracts/CustomerContracts';
import {BusinessInfoForm} from './(sections)/CustomerDiscriminator/BusinessInfoForm';
import {SelectFoundingPersonForm} from './(sections)/CustomerDiscriminator/SelectFoundingPersonForm';
import {CustomerInformation} from './(sections)/CustomerInformation/CustomerInformation';
import {CustomerOrders} from './(sections)/CustomerOrders/CustomerOrders';
import {Documents} from './(sections)/Documents/Documents';
import {Interests} from './(sections)/Interests/Interests';
import {Invoices} from './(sections)/Invoices/Invoices';
import {ServiceCases} from './(sections)/ServiceCases/ServiceCases';
import {Tires} from './(sections)/Tires/Tires';
import {Vehicles} from './(sections)/Vehicles/Vehicles';
import {BusinessCaseSourceDialog} from './components/BusinessCaseSourceDialog';
import {CustomerActivityPanel} from './components/CustomerActivityPanel/CustomerActivityPanel';
import {VEHICLES_GRID_CODE, VEHICLES_OWNERSHIP_GRID_CODE} from './utils/vehiclesGridCodes';

// eslint-disable-next-line complexity, max-lines-per-function, max-statements -- Pre-existing violation
export function CustomerDetail() {
  const navigate = useNavigate();
  const {isMultiLocale} = useTenant();
  const {customerId} = useCustomerUrl();
  const documentsCountBadge = useDocumentContextCount('customer', customerId);
  const invoicesCount = useDataGridTotalRows('customer-invoicing', {customer: [customerId]});
  const vehiclesCount = useDataGridTotalRows(VEHICLES_GRID_CODE, {customer: [customerId]});
  const vehiclesOwnershipCount = useDataGridTotalRows(VEHICLES_OWNERSHIP_GRID_CODE, {customerId});

  const gdprConsentsCount = useDataGridTotalRows(GDPR_CONSENTS_GRID_CODE, {customerId});

  const {businessCaseCount, interestCount} = useRecordCount({
    customerId,
    vehicleId: undefined,
  });

  const {
    data,
    isLoading: isCustomerLoading,
    isError: isCustomerErrored,
  } = useGetCustomerV2Query({customerId});

  const {isSmsActionDisabled, smsTooltipLabel} = useKonzultaSmsAvailability();

  const callCustomerAction = useCallCustomerAction({
    customerId,
    customer: data,
    'data-testid': testIds.customer.detail('callCustomer'),
  });

  const {isEnabled: isEmailConfigEnabled} = useEmailConfig();

  const {
    data: activeBusinessCase,
    isLoading: isActiveBCLoading,
    isError: isActiveBCErrored,
    error: activeBCError,
  } = useGetLastActiveBusinessCaseByCustomerQuery(
    {customerId},
    {
      selectFromResult: (res) => ({
        ...res,
        data: isNotNil(res?.data) ? head(res.data) : null,
      }),
    }
  );
  const {ownerRef: assistantEntitiesOwnerRef, synchronizeRef: synchronizeAssistantEntitiesRef} =
    useAssistantEntityContext(getCustomerAssistantEntities(customerId, data, activeBusinessCase));

  const {data: customerParticipation} = useGetParticipationQuery({
    resourceId: EntityResourceIds.customer,
    recordId: customerId,
  });

  const {count: tasksTodoCount} = useTasksCount({
    entityId: customerId,
    relation: EntityResourceIds.customer,
    state: 'TODO',
  });
  const {count: tasksInProgressCount} = useTasksCount({
    entityId: customerId,
    relation: EntityResourceIds.customer,
    state: 'IN_PROGRESS',
  });
  const tasksCountBadge = tasksTodoCount + tasksInProgressCount;

  const {customNoticesBadge} = useCustomNoticesBadge({
    resourceId: EntityResourceIds.customer,
    recordId: customerId,
  });

  const [
    hasDeleteCustomerPermission,
    canPurchaseVehicle,
    canSellVehicle,
    hasCreateBusinessCasePurchasePermission,
    hasCreateBusinessCaseSellingPermission,
    hasAddVehicleToBuyPermission,
    hasAddVehicleToSellPermission,
    canViewBusinessCaseSelling,
    canViewBusinessCasePurchase,
    canReadTag,
    hasAddCustomerTagPermission,
    hasUpdateCustomerTagPermission,
    hasAddCustomerTagToRecordPermission,
    hasRemoveCustomerTagFromRecordPermission,
    hasRemoveCustomerTagPermission,
    hasClearCustomerTagExpirationPermission,
    hasSetCustomerTagExpirationPermission,
    canReadBusinessCase,
    canReadServiceCase,
    canReadDocuments,
    canReadInterest,
    canReadPneuhotel,
    canReadArticle,
  ] = usePermissions({
    permissionKeys: [
      'deleteCustomer',
      'viewBusinessCasePurchase',
      'addVehicleToSell',
      'createBusinessCasePurchase',
      'createBusinessCaseSelling',
      'addVehicleToBuy',
      'addVehicleToSell',
      'viewBusinessCaseSelling',
      'viewBusinessCasePurchase',
      'customerTagRead',
      'customerTagAdd',
      'customerTagUpdate',
      'customerTagAddToRecord',
      'customerTagRemoveFromRecord',
      'customerTagRemove',
      'customerTagClearExpiration',
      'customerTagSetExpiration',
      'businessCaseRead',
      'serviceCaseRead',
      'readDocuments',
      'interestRead',
      'pneuhotelRead',
      'articleRead',
    ],
    scopes: {
      customerTagAddToRecord: {
        participation: customerParticipation,
        // Customer has no branchId
        branchId: 'SKIP',
      },
      customerTagRead: {
        participation: customerParticipation,
        // Customer has no branchId
        branchId: 'SKIP',
      },
      customerTagRemoveFromRecord: {
        participation: customerParticipation,
        // Customer has no branchId
        branchId: 'SKIP',
      },
      viewBusinessCasePurchase: {
        // BC creation/BC tab should be allowed regardless of participation scope
        participation: 'SKIP',
        // BC creation/BC tab should be allowed regardless of participation scope
        branchId: 'SKIP',
      },
      viewBusinessCaseSelling: {
        // BC creation/BC tab should be allowed regardless of participation scope
        participation: 'SKIP',
        // BC creation/BC tab should be allowed regardless of participation scope
        branchId: 'SKIP',
      },
      deleteCustomer: {
        participation: customerParticipation,
      },
      interestRead: {
        // interest creation/interest tab should be allowed regardless of participation scope
        participation: 'SKIP',
        // interest creation/interest tab should be allowed regardless of participation scope
        branchId: 'SKIP',
      },
      serviceCaseRead: {
        // Customer has no branchId
        branchId: 'SKIP',
        // Customer has no serviceCaseType
        serviceCaseType: 'SKIP',
      },
    },
  });

  const {
    data: lastDirectSale,
    isLoading: isLastDirectSaleLoading,
    isError: isLastDirectSaleError,
    error: lastDirectSaleError,
  } = useGetDirectSaleLastActiveByCustomerIdQuery(
    {customerId},
    {
      skip: !canReadArticle,
    }
  );

  const {
    data: lastDirectSaleVehicle,
    isLoading: isLastDirectSaleVehicleLoading,
    isError: isLastDirectSaleVehicleError,
    error: lastDirectSaleVehicleError,
  } = useGetVehicleV2Query(
    {vehicleId: lastDirectSale?.vehicle_id as string},
    {skip: isNilOrEmpty(lastDirectSale?.vehicle_id)}
  );

  const lastDirectSaleStateFlag = useStateFlag(lastDirectSale?.state);

  const canCreateBusinessCase =
    hasCreateBusinessCasePurchasePermission && hasAddVehicleToBuyPermission;
  const canCreateSellBusinessCase =
    hasCreateBusinessCaseSellingPermission && hasAddVehicleToSellPermission;

  const canSeePurchase = canPurchaseVehicle && canCreateBusinessCase;
  const canSeeSell = canSellVehicle && canCreateSellBusinessCase;

  const canCreateTag = hasAddCustomerTagPermission && hasUpdateCustomerTagPermission;

  const canAssignOrUnassignTag =
    hasAddCustomerTagToRecordPermission && hasRemoveCustomerTagFromRecordPermission;

  const canDeleteTag =
    hasRemoveCustomerTagPermission &&
    hasClearCustomerTagExpirationPermission &&
    hasSetCustomerTagExpirationPermission;

  const hasSalesLicense = canReadBusinessCase;

  const {isWorkshopModuleEnabled} = useIsLicenceEnabled();
  const serviceCasesCount = useDataGridTotalRows(
    'customer-service-case',
    {vehicleOwnerships: customerId},
    {skip: !isWorkshopModuleEnabled || !canReadServiceCase}
  );

  const visibleVehiclesCount =
    (canReadBusinessCase ? (vehiclesCount ?? 0) : 0) +
    (isWorkshopModuleEnabled ? (vehiclesOwnershipCount ?? 0) : 0);

  const {data: customerContracts} = useGetCustomerContractsByCustomerIdQuery(
    {customerId},
    {skip: !isWorkshopModuleEnabled}
  );

  const {data: customerServiceContracts} = useGetServiceContractsQuery(
    {customerId},
    {skip: !isWorkshopModuleEnabled}
  );
  const {
    data: lastServiceCase,
    isLoading: isLastServiceCaseLoading,
    isError: isLastServiceCaseError,
    error: lastServiceCaseError,
  } = useGetCustomerLastActiveServiceCaseQuery(
    {customerId},
    {skip: !isWorkshopModuleEnabled || !canReadServiceCase}
  );
  const {
    data: serviceCaseCustomer,
    isLoading: isServiceCaseCustomerLoading,
    isError: isServiceCaseCustomerError,
    error: serviceCaseCustomerError,
  } = useGetCustomerV2Query(
    {customerId: lastServiceCase?.customerId ?? ''},
    {skip: isNil(lastServiceCase?.customerId) || !isWorkshopModuleEnabled}
  );
  const {
    data: serviceCaseVehicle,
    isLoading: isServiceCaseVehicleLoading,
    isError: isServiceCaseVehicleError,
    error: serviceCaseVehicleError,
  } = useGetVehicleV2Query(
    {vehicleId: lastServiceCase?.vehicleId ?? ''},
    {skip: !lastServiceCase?.vehicleId || !isWorkshopModuleEnabled}
  );
  const {
    data: directSaleCustomer,
    isLoading: isDirectSaleCustomerLoading,
    isError: isDirectSaleCustomerError,
    error: directSaleCustomerError,
  } = useGetCustomerV2Query(
    {customerId: lastServiceCase?.customerId as string},
    {
      skip:
        isNilOrEmpty(lastServiceCase?.customerId) ||
        !isWorkshopModuleEnabled ||
        !canReadServiceCase,
    }
  );
  const {data: vehicleTires} = useGetTireVehicleCountQuery(
    {customerId},
    {
      skip: !canReadPneuhotel,
    }
  );
  const {data: customerTireSets, isSuccess: isCustomerTireSetsSuccess} = useGetTireSetCountQuery(
    {customerId, states: ['INSTOCK']},
    {
      skip: !canReadPneuhotel,
    }
  );

  const lastServiceCaseStateFlag = useStateFlag(lastServiceCase?.state);
  const lastServiceCaseOrdersStateFlag = useStateFlag(lastServiceCase?.ordersState);

  const [createBusinessCaseMutation, {isLoading: isCreateBusinessCaseLoading}] =
    useCreateBusinessCaseMutation();
  const [deleteCustomer, {isLoading: isDeleteCustomerLoading}] = useDeleteCustomerMutation();

  const {badgeSettings, checklistSettings} = useChecklist({
    recordId: customerId,
    resourceId: 'CUSTOMER',
  });

  const isLoading = any(isTrue, [
    isCustomerLoading,
    isActiveBCLoading,
    isLastServiceCaseLoading,
    isLastDirectSaleLoading,
    isLastDirectSaleVehicleLoading,
    isDirectSaleCustomerLoading,
    isServiceCaseCustomerLoading,
    isServiceCaseVehicleLoading,
  ]);

  const isError = any(isTrue, [
    isCustomerErrored,
    isActiveBCErrored && !is404ApiError(activeBCError),
    isLastServiceCaseError && !is404ApiError(lastServiceCaseError),
    isLastDirectSaleError && !is404ApiError(lastDirectSaleError),
    isLastDirectSaleVehicleError && !is404ApiError(lastDirectSaleVehicleError),
    isDirectSaleCustomerError && !is404ApiError(directSaleCustomerError),
    isServiceCaseCustomerError && !is404ApiError(serviceCaseCustomerError),
    isServiceCaseVehicleError && !is404ApiError(serviceCaseVehicleError),
  ]);

  const handleDelete = () =>
    openDeleteDialog({
      'data-testid': testIds.customer.detail('delete'),
      onConfirm: () =>
        deleteCustomer({
          customerId,
        })
          .unwrap()
          .then(() => {
            showNotification.success(i18n.t('entity.customer.notifications.deleteSuccess'));
            navigate(customerRoutes.overview);
          })
          .catch(handleApiError),
    });

  const handleChangeCustomerType = () => {
    if (data?.discriminator === 'BUSINESS') {
      openDialog(
        <SelectFoundingPersonForm customerId={customerId} contactPersons={data.contacts} />,
        {
          scrollBehavior: 'outside',
          title: i18n.t('entity.customer.actions.selectFoundingPerson'),
        }
      );
    } else {
      openDialog(<BusinessInfoForm customerId={customerId} addresses={data?.addresses ?? []} />, {
        scrollBehavior: 'outside',
        title: i18n.t('entity.customer.actions.businessInfo'),
      });
    }
  };

  const handleCreateBusinessCaseWithSource = (sourceCodeId: string) =>
    createBusinessCaseMutation({
      createBusinessCaseRequestBody: {
        customerId,
        sourceCodeId,
      },
    })
      .unwrap()
      .then((res) => {
        closeCurrentDialog();
        navigate(composePath(businessCaseRoutes.customer, {params: {id: res.id}}));
      })
      .catch((error: ApiError) => {
        handleApiError(error);
        throw error;
      });

  const createBusinessCase = () => {
    if (isFeatureEnabled(featureFlags.BC_SOURCE)) {
      openDialog(<BusinessCaseSourceDialog onConfirm={handleCreateBusinessCaseWithSource} />, {
        title: i18n.t('entity.interest.labels.source'),
      });
    } else {
      createBusinessCaseMutation({
        createBusinessCaseRequestBody: {
          customerId,
        },
      })
        .unwrap()
        .then((res) => navigate(composePath(businessCaseRoutes.customer, {params: {id: res.id}})))
        .catch(handleApiError);
    }
  };

  const navigateDirectSalesCreate = () =>
    navigate(composePath(warehouseRoutes.directSalesCreate, {queryParams: {customerId}}));

  const navigateInterestCreate = () =>
    navigate(composePath(interestRoutes.create, {queryParams: {customerId}}));

  const maximization = {
    enabled: true,
    title: `${i18n.t('entity.customer.labels.customer')}: ${getCustomerName(data) ?? EMPTY_PLACEHOLDER}`,
  };

  const navigationItems = buildArray<NavigationItem>()
    .add({
      id: customerSearchParams.customerDetail.customerInformation,
      href: composePath(customerRoutes.customerInformation, {
        params: {id: customerId},
      }),
      label: i18n.t('entity.customer.labels.customerInformation'),
      content: <CustomerInformation />,
      'data-testid': testIds.customer.detail('navigation-customerInformation'),
    })
    .when(isNotNil(data), {
      id: customerSearchParams.customerDetail.GDPRConsents,
      href: composePath(customerRoutes.GDPRConsents, {
        params: {id: customerId},
      }),
      label: i18n.t('entity.customer.labels.GDPRConsents'),
      content: (
        <Section>
          <GdprConsentsList
            maximization={maximization}
            businessCaseId={activeBusinessCase?.id}
            customerId={customerId}
          />
        </Section>
      ),
      'data-testid': testIds.customer.detail('navigation-GDPRConsents'),
      badge: toMenuBadge(gdprConsentsCount ?? 0),
    })
    .when(hasSalesLicense && (canPurchaseVehicle || canSellVehicle), {
      id: customerSearchParams.customerDetail.businessCases,
      href: composePath(customerRoutes.businessCases, {
        params: {id: customerId},
      }),
      label: i18n.t('entity.customer.labels.businessCases'),
      content: <BusinessCases maximization={maximization} />,
      'data-testid': testIds.customer.detail('navigation-businessCases'),
      badge: toMenuBadge(businessCaseCount?.count ?? 0),
    })
    .when(hasSalesLicense && canReadInterest, {
      id: customerSearchParams.customerDetail.interests,
      href: composePath(customerRoutes.interests, {
        params: {id: customerId},
      }),
      label: i18n.t('entity.customer.labels.interests'),
      content: <Interests maximization={maximization} />,
      'data-testid': testIds.customer.detail('navigation-interests'),
      badge: toMenuBadge(interestCount?.count ?? 0),
    })
    .add({
      id: customerSearchParams.customerDetail.vehicles,
      href: composePath(customerRoutes.vehicles, {
        params: {id: customerId},
      }),
      label: i18n.t('entity.customer.labels.vehicles'),
      content: (
        <Vehicles
          maximization={maximization}
          soldVehiclesCount={vehiclesCount}
          serviceVehiclesCount={vehiclesOwnershipCount}
        />
      ),
      hasSeparator: true,
      'data-testid': testIds.customer.detail('navigation-vehicles'),
      badge: toMenuBadge(visibleVehiclesCount),
    })
    .when(canReadDocuments, {
      id: customerSearchParams.customerDetail.documents,
      href: composePath(customerRoutes.documents, {
        params: {id: customerId},
      }),
      label: i18n.t('entity.customer.labels.documents'),
      content: <Documents maximization={maximization} />,
      'data-testid': testIds.customer.detail('navigation-documents'),
      badge: documentsCountBadge,
    })
    .add({
      id: customerSearchParams.customerDetail.invoices,
      href: composePath(customerRoutes.invoices, {
        params: {id: customerId},
      }),
      label: i18n.t('entity.customer.labels.invoices'),
      content: <Invoices maximization={maximization} />,
      'data-testid': testIds.customer.detail('navigation-invoices'),
      badge: toMenuBadge(invoicesCount ?? 0),
      hasSeparator: true,
    })
    .when(isWorkshopModuleEnabled && canReadServiceCase, {
      id: customerSearchParams.customerDetail.serviceCases,
      href: composePath(customerRoutes.serviceCases, {
        params: {id: customerId},
      }),
      label: i18n.t('page.workshop.labels.serviceCases'),
      content: <ServiceCases maximization={maximization} />,
      'data-testid': testIds.customer.detail('navigation-serviceCases'),
      badge: toMenuBadge(serviceCasesCount ?? 0),
    })
    .when(canReadPneuhotel && isWorkshopModuleEnabled, {
      id: 'tires',
      href: composePath(customerRoutes.tires, {
        params: {id: customerId},
      }),
      label: i18n.t('page.tiresInventory.labels.tires'),
      content: <Tires maximization={maximization} />,
      badge: isCustomerTireSetsSuccess
        ? {value: String(customerTireSets?.count ?? 0), colorScheme: 'neutral'}
        : undefined,
      isDisabled: !vehicleTires?.count,
      'data-testid': testIds.vehicles.detail('navigation-item-tires'),
    })
    .when(isWorkshopModuleEnabled, {
      id: customerSearchParams.customerDetail.customerContracts,
      href: composePath(customerRoutes.customerContracts, {
        params: {id: customerId},
      }),
      label: i18n.t('entity.customerContract.labels.customerContracts'),
      content: (
        <CustomerContracts data-testid={testIds.customer.detail('navigation-customerContracts')} />
      ),
      badge: toMenuBadge(customerContracts?.length ?? 0),
      'data-testid': testIds.customer.detail('navigation-customerContracts'),
    })
    .when(isWorkshopModuleEnabled, {
      id: customerSearchParams.customerDetail.customerOrders,
      href: composePath(customerRoutes.customerOrders, {
        params: {id: customerId},
      }),
      label: i18n.t('page.customer.labels.customerOrders'),
      content: <CustomerOrders maximization={maximization} />,
      badge: toMenuBadge(customerServiceContracts?.length ?? 0),
      hasSeparator: true,
      'data-testid': testIds.customer.detail('navigation-customerOrders'),
    })
    .whenFeatureEnabled(featureFlags.SALE_TASKS_CHECKLIST, {
      id: customerSearchParams.customerDetail.checklists,
      label: i18n.t('entity.checklist.labels.title'),
      'data-testid': testIds.customer.detail('navigation-checklists'),
      content: (
        <Section>
          <Checklists {...checklistSettings} contextTarget="customer" contextId={customerId} />
        </Section>
      ),
      badge: badgeSettings,
      href: composePath(customerRoutes.checklists, {
        params: {id: customerId},
      }),
    })
    .add({
      id: 'tasks',
      label: i18n.t('entity.task.labels.tasks'),
      href: composePath(customerRoutes.tasks, {params: {id: customerId}}),
      'data-testid': testIds.customer.detail('navigation-item-tasks'),
      badge: toMenuBadge(tasksCountBadge),
      content: (
        <TaskGridPage
          maximization={maximization}
          gridCode="customer-task"
          entityId={customerId}
          data-testid={testIds.customer.detail('tasks')}
        />
      ),
    })
    .whenFeatureEnabled(featureFlags.CUSTOM_NOTICES, {
      id: 'customNotices',
      href: composePath(customerRoutes.customNotices, {params: {id: customerId}}),
      label: i18n.t('general.customNotices.labels.title'),
      content: (
        <CustomNoticeList
          resourceId={EntityResourceIds.customer}
          recordId={customerId}
          data-testid={testIds.customer.detail('customNotices')}
        />
      ),
      badge: customNoticesBadge,
      hasSeparator: true,
      'data-testid': testIds.customer.detail('navigation-item-customNotices'),
    })
    .add({
      id: 'activityLogs',
      href: composePath(customerRoutes.activityLogs, {
        params: {id: customerId},
      }),
      label: i18n.t('general.labels.activityLogs'),
      content: <CustomerActivityLogs maximization={maximization} />,
      'data-testid': testIds.customer.detail('navigation-item-activityLogs'),
    })
    .add({
      id: 'communication',
      href: composePath(customerRoutes.communication, {
        params: {id: customerId},
      }),
      label: getCommunicationSectionTitle(),
      content: (
        <CommunicationSection
          maximization={maximization}
          customerId={customerId}
          recordId={customerId}
          resourceId={EntityResourceIds.customer}
          isContextLinkVisible
          data-testid="customer-communication"
        />
      ),
      'data-testid': testIds.customer.detail('navigation-item-communication'),
    });

  const entityHeaderProps = buildObject<
    Omit<DetailTemplateHeader, 'actions'> & {actions?: EntityHeaderAction[]}
  >()
    .title(getCustomerName(data) ?? EMPTY_PLACEHOLDER)
    .icon('social/person')
    .height(61)
    .parameters([data?.publicId])
    .recordId(data?.id)
    .resourceId(EntityResourceIds.customer)
    .controls(['ASSIGNEE'])
    .flags(
      buildArray<FlagProps>()
        .when(data && isCustomerNaturalPerson(data), {
          colorScheme: 'teal',
          label: i18n.t('general.labels.person'),
          'data-testid': testIds.customer.detail('person'),
        })
        .when(data && !isCustomerNaturalPerson(data), {
          colorScheme: 'purple',
          label: i18n.t('general.labels.company'),
          'data-testid': testIds.customer.detail('company'),
        })
        .when(isMultiLocale && isNotNil(data?.preferredLocale), {
          colorScheme: 'neutral',
          isSubtle: true,
          label: getLocaleLabel(data?.preferredLocale) ?? '',
          'data-testid': testIds.customer.detail('locale'),
        })
    )
    .primaryParameter(
      getCustomerPhoneNumber(data) ?? EMPTY_PLACEHOLDER,
      data && isCustomerNaturalPerson(data)
    )
    .secondaryParameter(
      getCustomerEmail(data) ?? EMPTY_PLACEHOLDER,
      data && isCustomerNaturalPerson(data)
    )
    .snippets(
      buildArray<SnippetProps>()
        .when(
          hasSalesLicense &&
            (isNotNil(activeBusinessCase?.id) &&
            (activeBusinessCase?.vehicleDataPartialInfo?.purchaseVehiclesPartialInfo?.length ||
              activeBusinessCase?.vehicleDataPartialInfo?.purchaseBrokerageVehiclesPartialInfo
                ?.length)
              ? canPurchaseVehicle
              : canSellVehicle),
          buildObject<SnippetProps>()
            .icon('places/business_center')
            .label(i18n.t('page.businessCase.labels.activeBusinessCase'))
            .title(
              head(
                activeBusinessCase?.vehicleDataPartialInfo?.purchaseBrokerageVehiclesPartialInfo ??
                  []
              )?.title ||
                head(activeBusinessCase?.vehicleDataPartialInfo?.purchaseVehiclesPartialInfo ?? [])
                  ?.title ||
                head(activeBusinessCase?.vehicleDataPartialInfo?.saleVehiclesPartialInfo ?? [])
                  ?.title ||
                i18n.t(`page.workshop.placeholders.noVehicle`)
            )
            .flags(
              buildArray<FlagProps>()
                .when(
                  activeBusinessCase?.businessCaseState,
                  getBusinessCaseStateFlag(
                    activeBusinessCase?.businessCaseState as BusinessCaseState
                  ) as FlagProps
                )
                .when(
                  activeBusinessCase?.businessCaseType,
                  getBusinessCaseTypeFlag(activeBusinessCase?.businessCaseType) as FlagProps
                )
            )
            .parameters(
              buildArray<Parameter>()
                .when(activeBusinessCase?.code, activeBusinessCase?.code)
                .when(
                  activeBusinessCase?.ownerFirstName && activeBusinessCase?.ownerLastName,
                  `${activeBusinessCase?.ownerFirstName} ${activeBusinessCase?.ownerLastName}`
                )
            )
            .href(
              activeBusinessCase?.id
                ? composePath(businessCaseRoutes.overview, {
                    params: {id: activeBusinessCase.id},
                  })
                : undefined,
              isNotNil(activeBusinessCase?.id)
            )
            .selectedContactId(
              isNotNil(activeBusinessCase?.ownerId) ? activeBusinessCase?.ownerId : undefined
            )
            .build()
        )
        .when(
          hasSalesLicense &&
            isNil(activeBusinessCase?.id) &&
            (canCreateBusinessCase || canCreateSellBusinessCase),
          {
            icon: 'places/business_center',
            label: i18n.t('page.businessCase.labels.activeBusinessCase'),
            placeholder: {
              title: i18n.t('page.businessCase.actions.create'),
              onClick: createBusinessCase,
            },
          }
        )
        .when(
          isNotNil(lastServiceCase?.id) && isWorkshopModuleEnabled && canReadServiceCase,
          buildObject<SnippetProps>()
            .icon('action/build')
            .label(i18n.t('page.vehicle.labels.lastActiveServiceCase'))
            .title(serviceCaseVehicle?.title ?? EMPTY_PLACEHOLDER)
            .flags(
              buildArray<FlagProps>()
                .when(lastServiceCaseStateFlag, lastServiceCaseStateFlag!)
                .when(lastServiceCaseOrdersStateFlag, lastServiceCaseOrdersStateFlag!)
            )
            .parameters(
              buildArray<Parameter>()
                .when(lastServiceCase?.number, lastServiceCase?.number)
                .when(serviceCaseCustomer, getCustomerName(serviceCaseCustomer))
            )
            .href(
              lastServiceCase?.id
                ? composePath(workshopRoutes.serviceCaseDetail, {
                    params: {id: lastServiceCase.id},
                  })
                : undefined,
              isNotNil(lastServiceCase?.id)
            )
            .build()
        )
        .when(isNil(lastServiceCase?.id) && isWorkshopModuleEnabled && canReadServiceCase, {
          icon: 'places/business_center',
          label: i18n.t('page.vehicle.labels.lastActiveServiceCase'),
          placeholder: {
            title: i18n.t('page.workshop.labels.newServiceCase'),
            onClick: () =>
              navigate(composePath(workshopRoutes.createServiceCase, {queryParams: {customerId}})),
          },
        })
        .when(
          isNotNil(lastDirectSale?.id) && isWorkshopModuleEnabled,
          buildObject<SnippetProps>()
            .icon('action/shopping_cart')
            .label(i18n.t('page.vehicle.labels.lastDirectSale'))
            .title(lastDirectSaleVehicle?.title ?? EMPTY_PLACEHOLDER)
            .flags(buildArray<FlagProps>().when(lastDirectSaleStateFlag, lastDirectSaleStateFlag!))
            .parameters(
              buildArray<Parameter>()
                .when(lastDirectSale?.number, lastDirectSale?.number)
                .when(directSaleCustomer, getCustomerName(directSaleCustomer))
            )
            .href(
              lastDirectSale?.id
                ? composePath(warehouseRoutes.directSalesDetailOverview, {
                    params: {id: lastDirectSale.id},
                  })
                : undefined,
              isNotNil(lastDirectSale?.id)
            )
            .build()
        )
        .when(isNil(lastDirectSale?.id) && isWorkshopModuleEnabled, {
          icon: 'places/business_center',
          label: i18n.t('page.vehicle.labels.lastDirectSale'),
          placeholder: {
            title: i18n.t('page.workshop.labels.createDirectSale'),
            onClick: navigateDirectSalesCreate,
          },
        })
    )
    .actions(
      buildArray<EntityHeaderAction>()
        .when(hasDeleteCustomerPermission || canReadArticle, {
          type: 'dropdown-iconButton',
          priority: 'secondary',
          menuItems: buildArray<DropdownActionMenuItem>()
            .when(isWorkshopModuleEnabled && canReadServiceCase, {
              label: i18n.t('page.workshop.labels.createDirectSale'),
              onClick: navigateDirectSalesCreate,
              isLoading: isDeleteCustomerLoading,
              'data-testid': testIds.customer.detail('createDirectSale'),
            })
            .when(hasDeleteCustomerPermission, {
              label: i18n.t('entity.customer.actions.delete'),
              onClick: handleDelete,
              isLoading: isDeleteCustomerLoading,
              'data-testid': testIds.customer.detail('delete'),
            })
            .add({
              label: i18n.t('entity.customer.actions.changeCustomerType'),
              onClick: handleChangeCustomerType,
              'data-testid': testIds.customer.detail('changeCustomerType'),
            }),
          'data-testid': testIds.customer.detail('vehicleHeader-more'),
        })
        .whenNotNil(callCustomerAction)
        .add({
          type: 'dropdown-button',
          variant: 'secondary',
          rightIcon: 'hardware/keyboard_arrow_down',
          title: i18n.t('general.actions.sendMessage'),
          menuItems: [
            {
              label: i18n.t('general.labels.email'),
              leftIcon: 'communication/email',
              isDisabled: !isEmailConfigEnabled,
              tooltip: !isEmailConfigEnabled
                ? {
                    label: i18n.t('general.notifications.setupEmailProvider'),
                    placement: 'top-start',
                  }
                : undefined,
              onClick: () =>
                openDialog(
                  <SendCustomerEmailForm
                    customerId={customerId}
                    recordId={customerId}
                    resourceId={EntityResourceIds.customer}
                  />,
                  {
                    withAdditionalFooter: true,
                    size: 'large',
                    title: i18n.t('general.actions.sendEmail'),
                  }
                ),
              'data-testid': testIds.customer.detail('sendEmail'),
            },
            {
              label: i18n.t('general.labels.sms'),
              leftIcon: 'communication/textsms',
              isDisabled: isSmsActionDisabled,
              tooltip: isSmsActionDisabled
                ? {
                    label: smsTooltipLabel,
                    placement: 'bottom-start',
                  }
                : undefined,
              onClick: () =>
                openDialog(
                  <SendCustomerSmsForm
                    customerId={customerId}
                    recordId={customerId}
                    resourceId={EntityResourceIds.customer}
                  />,
                  {
                    scrollBehavior: 'outside',
                    size: 'large',
                    title: i18n.t('general.labels.smsMessage'),
                  }
                ),
              'data-testid': testIds.customer.detail('sendSms'),
            },
          ],
          'data-testid': testIds.customer.detail('sendMessage'),
        })
        .when(isWorkshopModuleEnabled && canReadServiceCase, {
          type: 'button',
          variant:
            canReadBusinessCase &&
            ((canSeePurchase && canViewBusinessCasePurchase) ||
              (canSeeSell && canViewBusinessCaseSelling))
              ? 'secondary'
              : 'primary',
          title: i18n.t('page.workshop.labels.newServiceCase'),
          onClick: () =>
            navigate(composePath(workshopRoutes.createServiceCase, {queryParams: {customerId}})),
          'data-testid': testIds.customer.detail('createServiceCase'),
        })
        .when(hasSalesLicense && canReadInterest, {
          type: 'button',
          variant: 'secondary',
          title: i18n.t('entity.interest.actions.newInterest'),
          onClick: navigateInterestCreate,
          'data-testid': testIds.customer.detail('createInterest'),
        })
        .when(
          canReadBusinessCase &&
            ((canSeePurchase && canViewBusinessCasePurchase) ||
              (canSeeSell && canViewBusinessCaseSelling)),
          {
            type: 'button',
            variant: isWorkshopModuleEnabled && canReadServiceCase ? 'secondary' : 'primary',
            title: i18n.t('page.businessCase.actions.create'),
            onClick: createBusinessCase,
            isLoading: isCreateBusinessCaseLoading,
            'data-testid': testIds.customer.detail('createBusinessCase'),
          }
        )
    )
    .build();

  return (
    <>
      <div ref={assistantEntitiesOwnerRef} hidden>
        <span ref={synchronizeAssistantEntitiesRef} aria-hidden />
      </div>
      {match(data)
        .with(Pattern.nullish, () => null)
        .otherwise((customer) => (
          <Helmet title={i18n.t('page.customer.title', {name: getCustomerName(customer)})} />
        ))}
      <DataStatus isLoading={isLoading} isError={isError} minHeight={100}>
        <Page
          key={customerId}
          navigation={navigationItems}
          isFullHeight
          activityPanel={<CustomerActivityPanel customerId={customerId} />}
          header={
            <EntityHeader
              {...entityHeaderProps}
              permissions={{
                tag: {
                  isDeletable: canDeleteTag,
                  isUpdatable: canCreateTag,
                  isReadable: canReadTag,
                  isCreatable: canCreateTag,
                  isAssignable: canAssignOrUnassignTag,
                  isUnassignable: canAssignOrUnassignTag,
                },
              }}
              data-testid={testIds.customer.detail('customer-detail')}
            />
          }
          data-testid={testIds.customer.detail('customer-page')}
        />
      </DataStatus>
    </>
  );
}
